import { analyticsEvents, dataLayers } from "@/constants/data-layers";
import i18n from "@/i18n";
import { DataLayerEntry } from "@/types";

/**
 * Handles DataLayer entries
 */
export class DataLayerService {
  private readonly store: any;
  private readonly dataLayerMap: Map<string, DataLayerEntry>;
  private userId: null | string;
  private leadId: null | string;

  constructor(store: any) {
    this.store = store;
    this.userId = null;
    this.leadId = null;
    this.dataLayerMap = new Map<string, DataLayerEntry>();

    dataLayers.forEach((entry) => {
      this.dataLayerMap.set(entry.routeName, entry);
    });
  }

  /**
   * Push named entry from dataLayer config file
   * Outputs error to console if build environment is not set to "production"
   * @param name
   */
  public push(name: string): void {
    const dataLayer = this.dataLayerMap.get(name);

    if (null != this.store.state.previousStep) {
      if (dataLayer && Array.isArray(window.dataLayer)) {
        if (
          (
            dataLayer.allowedSourceRoutes
            && dataLayer.allowedSourceRoutes.includes(this.store.state.previousStep)
          )
          ||
          (
            !dataLayer.allowedSourceRoutes
            && (

              !dataLayer.excludedSourceRoutes
              || (
                dataLayer.excludedSourceRoutes
                && !dataLayer.excludedSourceRoutes.includes(this.store.state.previousStep)
              )
            )
          )
        ) {
          let data;

          if (Array.isArray(dataLayer.dataLayer)) {
            data = dataLayer.dataLayer;
          } else {
            data = [dataLayer.dataLayer];
          }

          for (let entry of data) {
            entry = this.replaceTokensRecursively(entry);
            window.dataLayer.push(entry);
          }
        }
      } else {
        if (process.env.NODE_ENV !== "production") {
          console.error("There is no dataLayer specified for route '" + name + "'");
        }
      }
    }
  }

  public pushEntry(key: string) {
    if ((window as any).dataLayer instanceof [].constructor) {
      if (analyticsEvents[key]) {
        (window as any).dataLayer.push(analyticsEvents[key]);
      }
    }
  }

  public setData(userId: string, leadId: string) {
    this.userId = userId;
    this.leadId = leadId;
  }

  /**
   * Recursively call token replacer on all dataLayer entries
   * @param obj
   */
  private replaceTokensRecursively(obj: any): object {
    for (const entry in obj) {
      if (obj.hasOwnProperty(entry)) {
        if (typeof obj[entry] === "object") {
          obj[entry] = this.replaceTokensRecursively(obj[entry]);
        } else if (typeof obj[entry] === "string") {
          obj[entry] = this.replaceToken(obj[entry]);
        }
      }
    }

    return obj;
  }

  /**
   * Replace all {{userId}} and {{leadId}} tokens in passed entry
   * @param entry
   */
  private replaceToken(entry: string): string {
    entry += "";

    let matches = (entry as any).matchAll(/{{userId}}/g);
    for (const match of matches) {
      entry = entry.replace("{{userId}}", this.userId as string);
    }

    matches = (entry as any).matchAll(/{{leadId}}/g);
    for (const match of matches) {
      entry = entry.replace("{{leadId}}", this.leadId as string);
    }

    matches = (entry as any).matchAll(/{{language}}/g);
    for (const match of matches) {
      entry = entry.replace("{{language}}", i18n.locale);
    }

    return entry;
  }
}
